import { useState, useEffect } from "react";
// import { getStoredMedallionCollection, saveMedallionCollection } from "../../utils/profileUtils";
import { UserProfileContext } from "./context";

export const UserProfileProvider = ({ children }) => {
  // Only store non-sensitive fields in localStorage; hydrate PII from SDK at runtime
  const [userProfile, setUserProfile] = useState(() => {
    const savedProfile = localStorage.getItem("userProfile");
    if (savedProfile) {
      return JSON.parse(savedProfile);
    }
    return {
      isLoggedIn: false,
      preferences: {},
      collections: [],
    };
  });

  useEffect(() => {
    // Hydrate sensitive info from SDK at runtime
    if (window.nbc?.identity?.user) {
      setUserProfile(prev => ({
        ...prev,
        // These fields are only in memory, not persisted
        firstName: window.nbc.identity.user.givenName || '',
        lastName: window.nbc.identity.user.sn || '',
        userId: window.nbc.identity.user._id || '',
        email: window.nbc.identity.user.mail || '',
      }));
    }
    // Optionally, hydrate non-sensitive fields from localStorage
    const savedProfile = localStorage.getItem("userProfile");
    if (savedProfile) {
      const { isLoggedIn, preferences, collections } = JSON.parse(savedProfile);
      setUserProfile(prev => ({
        ...prev,
        isLoggedIn: isLoggedIn || false,
        preferences: preferences || {},
        collections: collections || [],
      }));
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "userProfile" && event.newValue) {
        console.log("Storage event detected in UserProfileContext");
        try {
          const newProfile = JSON.parse(event.newValue);
          console.log("New profile from storage event:", newProfile);
          setUserProfile(newProfile);
        } catch (error) {
          console.error("Error parsing userProfile from storage event:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const createProfile = (userData) => {
    const now = new Date();

    setUserProfile(prev => ({
      ...prev,
      userId: userData._id,
      firstName: userData.givenName,
      lastName: userData.sn,
      email: userData.mail,
      joinDate: now.toISOString(),
      isLoggedIn: true,
      preferences: userData.preferences || {},
      collections: [],
    }));

    return userData._id;
  };

  useEffect(() => {

    const profileString = JSON.stringify(userProfile);
    if (profileString !== localStorage.getItem("userProfile")) {
      localStorage.setItem("userProfile", profileString);
      try {
        window.dispatchEvent(
          new StorageEvent("storage", {
            key: "userProfile",
            newValue: profileString,
            storageArea: localStorage,
          })
        );
      } catch (e) {
        console.error("Error dispatching storage event:", e);
      }
    }
  }, [userProfile]);

  // Only checks non-sensitive fields
  // Enhanced login: merge medallionCollection if present
  const login = () => {
    const savedProfile = JSON.parse(
      localStorage.getItem("userProfile") || "{}"
    );
    if (savedProfile.isLoggedIn) {
      setUserProfile(prev => ({
        ...prev,
        isLoggedIn: true,
        preferences: savedProfile.preferences || {},
      }));
      // Optionally clear medallionCollection after restoring
      // localStorage.removeItem("medallionCollection");
      return true;
    }
    return false;
  };

  // Enhanced logout: persist collections before clearing userProfile
  const logout = () => {
    setUserProfile((prev) => {

      // Only clear user-specific keys, NOT FANFEST2025_APP:deviceId or medallionCollection
      localStorage.removeItem("userProfile");
      // DO NOT remove medallionCollection or FANFEST2025_APP:deviceId

      // Remove all sessionStorage keys except 'ageVerified' and 'onboardingComplete'
      const sessionKeysToPreserve = ["ageVerified", "onboardingComplete"];
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (!sessionKeysToPreserve.includes(key)) {
          sessionStorage.removeItem(key);
        }
      }

      return {
        ...prev,
        isLoggedIn: false,
        // Optionally clear sensitive fields in memory
        firstName: '',
        lastName: '',
        userId: '',
        email: '',
      };
    });
  };

  // No longer persists PII; only sets in-memory state
  const updateProfile = (updates) => {
    setUserProfile((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const value = {
    userProfile,
    createProfile,
    login,
    logout,
    updateProfile

  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};

export default UserProfileContext;
